import { clock } from '@tabel/shared/agent-events/texts';
import type { TimelineRow } from '@tabel/shared/agent-events/timeline';
import type { AgentHealth } from '@tabel/shared/connector/protocol';
import type { AgentId } from '@tabel/shared/connector/versions';
import type { ConnectorView, QueueItem, ThreadView } from '@tabel/shared/schemas/agent-client';
import {
  Archive,
  Bot,
  Check,
  CircleAlert,
  Copy,
  Info,
  type LucideIcon,
  MessageSquare,
  RefreshCw,
  TriangleAlert,
  WifiOff,
} from 'lucide-react';
import { useState } from 'react';
import { AGENT_STATE_LABEL, PC_LABEL } from '../../pages/MyAgents/texts';
import { Callout } from '../ui';
import { memoryLevel } from './MemoryRing';
import { LABEL, T, TITLE, agentName, say } from './texts';

/*
 * Состояния панели «Помощник» (T102; specs/006-agent-client/contracts/ui.md §3; FR-038, мокап States.dc.html). У
 * каждого состояния свой текст и одно действие. Полоса — Callout дизайн-системы под шапкой, действие — ссылкой в конце.
 * Тринадцать состояний таблицы §3:
 * - приветствие — в ленте до первого сообщения (`Greeting`): заголовок, одна фраза, подсказки по странице;
 * - агент работает — строки шагов и ответ в ленте, действие — кнопка «Остановить агента» в поле сообщения;
 * - агент остановлен — строка ленты с сервера «Агент остановлен. Это не ошибка.», без действия;
 * - остальные десять — полосы (порог памяти — `memoryLevel` кольца «Память разговора») (`stripOf` → `StateStrip`): компьютер не в сети, вход в агента истёк или не выполнен,
 *   агент не вошёл в табель, лимит модели, ошибка модели, память 90 %, модели не загрузились, разговор в архиве, лента
 *   догружается, только чтение.
 * Ещё две полосы того же вида: компьютера нет или он отключён и агент не готов по другой причине (не установлен, нужна
 * новая версия, нужен вход ключом API) — действие «Открыть «Мои ИИ-клиенты»».
 * Полоса одна: самая важная по порядку `stripOf`. Время — по времени объекта.
 */

export type StripState =
  | { kind: 'archived' }
  | { kind: 'lagging' }
  | { kind: 'no_computer' }
  | { kind: 'disabled'; name: string }
  | { kind: 'offline'; name: string; since: string | null }
  | { kind: 'login'; agent: AgentId; expired: boolean; command: string | null }
  | { kind: 'not_linked'; agent: AgentId; command: string | null }
  | { kind: 'not_ready'; agent: AgentId; reason: string }
  | { kind: 'rate_limit'; at: string | null; runId: string; attempt: number | null }
  | { kind: 'model_error'; title: string; text: string; runId: string; rowKey: string }
  | { kind: 'read_only' }
  | { kind: 'memory'; percent: number }
  | { kind: 'models_fallback' };

export interface StripInput {
  /** Разговор; `null` — новый разговор, ещё без сообщений. */
  thread: ThreadView | null;
  /** Компьютер разговора (или выбранный для нового). */
  computer: ConnectorView | null;
  health: AgentHealth | null;
  agent: AgentId | null;
  rows: readonly TimelineRow[];
  queue: readonly QueueItem[];
  /** Показан запасной список моделей. */
  modelsFallback: boolean;
  /** Лента отстаёт от журнала. */
  lagging: boolean;
  /** Список компьютеров загружен: «компьютера нет» — правда, а не загрузка. */
  computersKnown: boolean;
}

/** Последняя работа агента закончилась ошибкой: её номер и системная строка ошибки. */
function lastError(rows: readonly TimelineRow[]) {
  const last = [...rows].reverse().find((r) => r.kind === 'turn');
  if (!last || last.kind !== 'turn' || last.outcome !== 'error' || !last.runId) return null;
  const sys = [...rows]
    .reverse()
    .find((r) => r.kind === 'system' && r.tone === 'bad' && r.runId === last.runId);
  return { runId: last.runId, sys: sys?.kind === 'system' ? sys : null };
}

/** Самая важная полоса или `null`. */
export function stripOf(i: StripInput): StripState | null {
  if (i.thread?.archived) return { kind: 'archived' };
  if (i.lagging) return { kind: 'lagging' };
  const pc = i.computer;
  if (!pc) return i.computersKnown ? { kind: 'no_computer' } : null;
  if (pc.phase === 'disabled') return { kind: 'disabled', name: pc.name };
  if (!pc.online) return { kind: 'offline', name: pc.name, since: pc.lastSeenAt };
  const h = i.health;
  const agent = i.agent;
  if (h && agent) {
    if (h.state === 'login_expired' || h.state === 'logged_out')
      return { kind: 'login', agent, expired: h.state === 'login_expired', command: h.loginCommand };
    if (h.state === 'not_linked') return { kind: 'not_linked', agent, command: h.linkCommand };
    if (h.state !== 'ready') return { kind: 'not_ready', agent, reason: AGENT_STATE_LABEL[h.state] };
    if (h.flags.subscriptionBlocked)
      return { kind: 'not_ready', agent, reason: PC_LABEL.subscriptionBlocked };
  }
  const err = lastError(i.rows);
  if (err) {
    const retry = i.queue.find((q) => q.reason === 'retry' && q.sendAt !== null);
    if (retry)
      return {
        kind: 'rate_limit',
        at: retry.sendAt,
        runId: retry.retryOf ?? err.runId,
        attempt: retry.attempt,
      };
    if (err.sys)
      return {
        kind: 'model_error',
        title: err.sys.title,
        text: err.sys.text,
        runId: err.runId,
        rowKey: err.sys.key,
      };
  }
  if (i.thread?.readOnly) return { kind: 'read_only' };
  const mem = i.thread?.memPercent ?? null;
  if (mem !== null && memoryLevel(mem) === 'full') return { kind: 'memory', percent: mem };
  if (i.modelsFallback) return { kind: 'models_fallback' };
  return null;
}

export interface StripActions {
  openMyAgents: () => void;
  /** «Проверить <агента>» — проверка агента сейчас. */
  check: () => void;
  /** «Не повторять» — повтор после лимита. */
  cancelRetry: (runId: string) => void;
  /** «Повторить вопрос» после ошибки. */
  retry: (runId: string) => void;
  newThread: () => void;
  reloadModels: () => void;
  /** «Вернуть из архива». */
  restore: () => void;
  /** «Загрузить сейчас». */
  reload: () => void;
}

type Tone = 'neutral' | 'accent' | 'warn' | 'bad';

interface View {
  tone: Tone;
  icon: LucideIcon;
  title: string;
  text: string | null;
  command?: string | null;
  action: { label: string; run: () => void };
}

function viewOf(s: StripState, tz: string, a: StripActions, checking: boolean): View {
  const check = (agent: AgentId) => ({
    label: checking ? LABEL.checking : say.check(agentName(agent)),
    run: a.check,
  });
  const myAgents = { label: LABEL.myAgents, run: a.openMyAgents };
  switch (s.kind) {
    case 'archived':
      return {
        tone: 'neutral',
        icon: Archive,
        title: T.archived,
        text: T.archivedText,
        action: { label: LABEL.restore, run: a.restore },
      };
    case 'lagging':
      return {
        tone: 'accent',
        icon: RefreshCw,
        title: T.lagging,
        text: T.laggingText,
        action: { label: LABEL.reloadNow, run: a.reload },
      };
    case 'no_computer':
      return { tone: 'warn', icon: WifiOff, title: T.noComputer, text: T.noComputerFix, action: myAgents };
    case 'disabled':
      return {
        tone: 'warn',
        icon: WifiOff,
        title: say.computerDisabled(s.name),
        text: T.noComputerFix,
        action: myAgents,
      };
    case 'offline':
      return {
        tone: 'warn',
        icon: WifiOff,
        title: say.offline(s.name, s.since ? clock(s.since, tz) : null),
        text: T.offlineWait,
        action: myAgents,
      };
    case 'login':
      return {
        tone: 'warn',
        icon: TriangleAlert,
        title: s.expired ? say.loginExpired(agentName(s.agent)) : say.loggedOut(agentName(s.agent)),
        text: T.loginFix,
        command: s.command,
        action: check(s.agent),
      };
    case 'not_linked':
      return {
        tone: 'warn',
        icon: TriangleAlert,
        title: say.notLinked(agentName(s.agent)),
        text: T.linkFix,
        command: s.command,
        action: check(s.agent),
      };
    case 'not_ready':
      return {
        tone: 'warn',
        icon: TriangleAlert,
        title: say.notReady(agentName(s.agent), s.reason),
        text: T.agentsPage,
        action: myAgents,
      };
    case 'rate_limit':
      return {
        tone: 'warn',
        icon: TriangleAlert,
        title: T.rateLimit,
        text: say.retryAt(s.at ? clock(s.at, tz) : null, s.attempt),
        action: { label: LABEL.noRetry, run: () => a.cancelRetry(s.runId) },
      };
    case 'model_error':
      return {
        tone: 'bad',
        icon: CircleAlert,
        title: s.title,
        text: s.text,
        action: { label: LABEL.retryQuestion, run: () => a.retry(s.runId) },
      };
    case 'read_only':
      return {
        tone: 'accent',
        icon: Info,
        title: T.readOnly,
        text: null,
        action: { label: checking ? LABEL.checking : LABEL.checkAgent, run: a.check },
      };
    case 'memory':
      return {
        tone: 'warn',
        icon: TriangleAlert,
        title: say.memoryFull(s.percent),
        text: T.memoryWarn,
        action: { label: LABEL.startNew, run: a.newThread },
      };
    case 'models_fallback':
      return {
        tone: 'accent',
        icon: Info,
        title: T.modelsFailed,
        text: T.modelsFallback,
        action: { label: LABEL.reloadModels, run: a.reloadModels },
      };
  }
}

/** Ссылка-действие полосы: на сенсорном экране не ниже 40 px. */
export const STRIP_LINK =
  'inline-flex items-center rounded-[4px] text-left text-[13px] font-medium text-accent hover:underline disabled:text-ink-3 disabled:no-underline max-tablet:min-h-10';

/** Команда для терминала со «Скопировать»: строит коннектор. Несколько строк — копируется первая (contracts/ui.md §4). */
function Command({ text }: { text: string }) {
  const [copied, setCopied] = useState<'idle' | 'ok' | 'failed'>('idle');
  const first = text.split('\n')[0] ?? text;
  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('буфер обмена недоступен');
      await navigator.clipboard.writeText(first);
      setCopied('ok');
    } catch {
      setCopied('failed');
    }
  };
  return (
    <div className="flex flex-col gap-1">
      <div className="flex min-w-0 items-center gap-2 rounded-control border border-line bg-surface py-1 pr-1 pl-3">
        <code
          aria-label={LABEL.command}
          className="min-w-0 flex-1 font-mono text-[12px] break-all whitespace-pre-wrap text-ink"
        >
          {text}
        </code>
        <button
          type="button"
          onClick={() => void copy()}
          className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-control px-2.5 text-[12.5px] font-medium text-ink-2 hover:bg-subtle hover:text-ink max-tablet:min-h-10"
        >
          {copied === 'ok' ? (
            <Check size={16} strokeWidth={1.75} aria-hidden />
          ) : (
            <Copy size={16} strokeWidth={1.75} aria-hidden />
          )}
          {copied === 'ok' ? LABEL.copied : LABEL.copy}
        </button>
      </div>
      {copied === 'failed' && <p className="text-[12px] text-warn">{T.copyFailed}</p>}
    </div>
  );
}

/** Полоса состояния под шапкой: текст и одно действие ссылкой в конце. */
export function StateStrip({
  state,
  tz,
  actions,
  checking = false,
}: {
  state: StripState;
  tz: string;
  actions: StripActions;
  /** Идёт проверка агента: ссылка «Проверка…». */
  checking?: boolean;
}) {
  const v = viewOf(state, tz, actions, checking);
  return (
    <div data-strip={state.kind} className="shrink-0 px-6 pt-3 max-sm:px-4">
      <div role="status">
        <Callout tone={v.tone} icon={v.icon}>
          <div className="flex min-w-0 flex-col gap-1.5">
            <p className="break-words">
              <span className="font-semibold">{v.title}</span>
              {v.text && <> {v.text}</>}
            </p>
            {v.command && <Command text={v.command} />}
            <p>
              <button
                type="button"
                data-strip-action
                disabled={checking && v.action.label === LABEL.checking}
                onClick={v.action.run}
                className={STRIP_LINK}
              >
                {v.action.label}
              </button>
            </p>
          </div>
        </Callout>
      </div>
    </div>
  );
}

/**
 * Приветствие до первого сообщения (contracts/ui.md §3, FR-038): заголовок, одна фраза и подсказки по странице.
 * Подсказка сразу начинает разговор, как в мокапе. Пока разговор подключается, приветствия нет — его не рисуют.
 */
export function Greeting({
  hints,
  onPick,
  disabled = false,
}: {
  hints: readonly string[];
  onPick: (text: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="mt-auto flex flex-col gap-2">
      <div className="flex flex-col items-start gap-2 pb-2">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-card bg-accent-soft text-accent">
          <Bot size={20} strokeWidth={1.75} aria-hidden />
        </span>
        <h3 className="text-[15px] font-semibold text-ink">{TITLE.hello}</h3>
        <p className="text-[13px] text-ink-2">{T.hello}</p>
      </div>
      {hints.length > 0 && (
        <>
          <p className="text-[11px] leading-[1.4] font-semibold tracking-[0.06em] text-ink-3 uppercase">
            {TITLE.examples}
          </p>
          <div className="flex flex-col gap-0.5">
            {hints.map((h) => (
              <button
                key={h}
                type="button"
                disabled={disabled}
                onClick={() => onPick(h)}
                className="flex min-h-10 w-full items-center gap-2.5 rounded-control px-2 py-1.5 text-left text-[14px] text-ink transition-colors hover:bg-subtle disabled:text-ink-3"
              >
                <MessageSquare size={16} strokeWidth={1.75} className="shrink-0 text-accent" aria-hidden />
                <span className="min-w-0">{h}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
